import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  message,
  Row,
  Select,
  Space,
  Table,
} from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import QuietEmpty from '@/components/QuietEmpty';
import { BusinessModal } from '@/modules/zy-crm/components/business-modal';
import modalStyles from '@/modules/zy-crm/components/business-modal/style.module.less';
import { CustomerReferenceLink } from '@/modules/zy-crm/components/customer-detail-drawer';
import { FormSectionDivider } from '@/modules/zy-crm/components/form-section-divider';
import { MemberPicker } from '@/modules/zy-crm/components/member-picker';
import { OpportunityReferenceLink } from '@/modules/zy-crm/components/opportunity-detail-drawer';
import {
  ProductCenterFormSection,
  type ProductCenterRow,
  type ProductCenterSelection,
} from '@/modules/zy-crm/components/product-center-form-section';
import {
  mapOpportunityProductToRow,
  type ProductRow,
  toQuoteApiProductRow,
} from '@/modules/zy-crm/components/quote-editor-modal/product-flow';
import { useMemberDirectory } from '@/modules/zy-crm/member-directory';
import { resolveCrmScopedViewModeFromPathname } from '@/modules/zy-crm/utils/crm-view';
import {
  formatCrmDate,
  resolveRelationText,
  toApiDateRangeEnd,
  toApiDateRangeStart,
} from '@/modules/zy-crm/utils/display';
import { getResponsiveModalWidth } from '@/modules/zy-crm/utils/modal-width';
import { orgDirectoryAdapter } from '@/modules/zy-crm/utils/org-directory-adapter';
import { queryOpportunityDrawerProfile } from '@/services/bff/opportunity.api';
import type { QuoteItem } from '@/services/bff/types/quote';
import { useOpenPlatformConfig } from '@/services/openPlatformToken';
import { createAppRequest } from '@/services/request';

const request = createAppRequest();
const TOTAL_DIFF_THRESHOLD = 0.01;
const NEW_PRODUCT_ROW_KEY = '__new_product__';
const MemberPickerField = MemberPicker as unknown as React.FC<any>;

function getCurrentPathname() {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.location.pathname || '';
}

type OptionItem = { label: string; value: string; raw?: any };

type QuoteCodeRule = {
  prefix: string;
  dateFormat: 'NONE' | 'YYMM' | 'YYYYMMDD';
  seqLength: number;
  resetCycle: 'NONE' | 'YEAR' | 'MONTH';
  seqCurrent: number;
  seqPeriod: string;
};

type EditableProductRow = ProductRow & {
  isNew?: boolean;
};

type QuoteEditorMode = 'create' | 'edit';

export type QuoteEditorContext = {
  customerUuid?: string;
  customerName?: string;
  lockCustomer?: boolean;
  contactUuid?: string;
  contactName?: string;
  opportunityUuid?: string;
  opportunityName?: string;
  ownerUserUuid?: string;
};

type QuoteEditorValues = Record<string, any>;

type QuoteEditorModalProps = {
  open: boolean;
  mode: QuoteEditorMode;
  quoteUuid?: string;
  context?: QuoteEditorContext;
  zIndex?: number;
  onCancel: () => void;
  onSuccess: (record?: QuoteItem) => void;
};

const DEFAULT_QUOTE_CODE_RULE: QuoteCodeRule = {
  prefix: 'QT',
  dateFormat: 'YYYYMMDD',
  seqLength: 4,
  resetCycle: 'NONE',
  seqCurrent: 0,
  seqPeriod: '',
};

const _normalizeText = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const toNumber = (value: unknown) => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return 0;
  return n;
};

const toAmount = (value: number, precision = 2) => value.toFixed(precision);

const toDateValue = (value: unknown): Dayjs | undefined => {
  if (!value) return undefined;
  const d = dayjs(String(value));
  return d.isValid() ? d : undefined;
};

const toDatePayload = (value: unknown, endOfDay = false) => {
  if (!value) return undefined;
  if (dayjs.isDayjs(value)) {
    return endOfDay ? toApiDateRangeEnd(value) : toApiDateRangeStart(value);
  }
  const d = dayjs(String(value));
  if (!d.isValid()) return undefined;
  return endOfDay ? toApiDateRangeEnd(d) : toApiDateRangeStart(d);
};

const normalizeProductRows = (rows: ProductRow[]) =>
  rows.map((row) => {
    const quantity = toNumber(row.quantity || 1);
    const quotePrice = toNumber(row.quotePrice);
    const totalPrice = quantity * quotePrice;
    return {
      ...row,
      productName: row.productName || '',
      productType: row.productType || undefined,
      productUnit: row.productUnit || undefined,
      quantity: toAmount(quantity),
      standardPrice: toAmount(toNumber(row.standardPrice)),
      quotePrice: toAmount(quotePrice),
      totalPrice: toAmount(totalPrice),
      remark: row.remark || undefined,
    };
  });

const normalizeEditableProductRow = (
  row?: Partial<EditableProductRow>,
): EditableProductRow => {
  const normalized = normalizeProductRows([
    {
      key: String(row?.key || '').trim() || NEW_PRODUCT_ROW_KEY,
      uuid: row?.uuid,
      offeringUuid: row?.offeringUuid,
      offeringVersionUuid: row?.offeringVersionUuid,
      productName: row?.productName || '',
      productCode: row?.productCode,
      productType: row?.productType,
      productUnit: row?.productUnit,
      versionNo: row?.versionNo,
      standardPrice: row?.standardPrice ?? '0.00',
      selectionSnapshot: row?.selectionSnapshot,
      quotePrice: row?.quotePrice ?? '0.00',
      quantity: row?.quantity ?? '1.00',
      totalPrice: row?.totalPrice ?? '0.00',
      remark: row?.remark,
    },
  ])[0];

  return {
    ...normalized,
    isNew: Boolean(row?.isNew),
  };
};

const mapQuoteProductToRow = (item: any, fallbackKey: string): ProductRow => ({
  key: String(item?.uuid || item?.offeringVersionUuid || fallbackKey),
  uuid: item?.uuid,
  offeringUuid: item?.offeringUuid,
  offeringVersionUuid: item?.offeringVersionUuid,
  productName: item?.productName || '',
  productCode: item?.productCode,
  productType: item?.productType,
  productUnit: item?.productUnit,
  versionNo: item?.versionNo,
  standardPrice: String(item?.standardPrice ?? '0'),
  selectionSnapshot: item?.selectionSnapshot,
  quotePrice: String(item?.quotePrice ?? '0'),
  quantity: String(item?.quantity ?? '1'),
  totalPrice: String(item?.totalPrice ?? '0'),
  rowSort: item?.rowSort,
  remark: item?.remark,
});

const hasMeaningfulProductRows = (rows: ProductRow[]) =>
  rows.some(
    (item) =>
      item.offeringVersionUuid &&
      item.productName &&
      toNumber(item.quantity) > 0,
  );

const readDetailPayload = (resp: any) => {
  if (!resp || typeof resp !== 'object') return undefined;
  if (typeof resp.uuid === 'string') return resp;
  const nested =
    resp?.data ??
    resp?.result ??
    resp?.payload ??
    resp?.response?.data ??
    undefined;
  if (nested && typeof nested === 'object' && typeof nested.uuid === 'string')
    return nested;
  return undefined;
};

const readDataList = (resp: any) => {
  const candidates = [
    resp?.list,
    resp?.items,
    resp?.data?.list,
    resp?.data?.items,
    resp?.data,
    resp?.result?.list,
    resp?.result?.items,
    resp?.result,
    resp?.payload?.list,
    resp?.payload?.items,
    resp?.payload,
  ];
  const matched = candidates.find((item) => Array.isArray(item));
  return Array.isArray(matched) ? matched : [];
};

const readDataTotal = (resp: any, fallback: number) => {
  const candidates = [
    resp?.total,
    resp?.data?.total,
    resp?.result?.total,
    resp?.payload?.total,
    resp?.count,
    resp?.data?.count,
  ];
  for (const candidate of candidates) {
    const total = Number(candidate);
    if (Number.isFinite(total) && total >= 0) return total;
  }
  return fallback;
};

const mergeOptionItem = (
  options: OptionItem[],
  value?: string,
  label?: string,
) => {
  const normalizedValue = String(value || '').trim();
  if (!normalizedValue) return options;
  if (options.some((item) => item.value === normalizedValue)) {
    return options;
  }
  return [
    {
      value: normalizedValue,
      label: String(label || normalizedValue),
    },
    ...options,
  ];
};

export function QuoteEditorModal({
  open,
  mode,
  quoteUuid,
  context,
  zIndex,
  onCancel,
  onSuccess,
}: QuoteEditorModalProps) {
  const [form] = Form.useForm<QuoteEditorValues>();
  const pageViewMode = resolveCrmScopedViewModeFromPathname(
    getCurrentPathname(),
    'quotes',
  );
  const [submitting, setSubmitting] = useState(false);
  const [customerOptions, setCustomerOptions] = useState<OptionItem[]>([]);
  const [contactOptions, setContactOptions] = useState<OptionItem[]>([]);
  const [opportunityOptions, setOpportunityOptions] = useState<OptionItem[]>(
    [],
  );
  const [productRows, setProductRows] = useState<ProductCenterRow[]>([]);
  const [editingProductKey, setEditingProductKey] = useState<string>();
  const [productDraftRow, setProductDraftRow] = useState<
    EditableProductRow | undefined
  >(undefined);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRange, setHistoryRange] = useState<
    'lastMonth' | 'halfYear' | 'thisYear'
  >('lastMonth');
  const [historyKeywordInput, setHistoryKeywordInput] = useState('');
  const [historyKeyword, setHistoryKeyword] = useState('');
  const [historyCustomerUuid, setHistoryCustomerUuid] = useState<
    string | undefined
  >(undefined);
  const [historySortBy, setHistorySortBy] = useState<
    'quoteDate' | 'createdAt' | 'totalPrice' | 'code'
  >('quoteDate');
  const [historySortOrder, setHistorySortOrder] = useState<'ASC' | 'DESC'>(
    'DESC',
  );
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(8);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyList, setHistoryList] = useState<QuoteItem[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [versionOpen, setVersionOpen] = useState(false);
  const [versionLoading, _setVersionLoading] = useState(false);
  const [versionTimeline, _setVersionTimeline] = useState<any[]>([]);
  const [versionDiff, _setVersionDiff] = useState<any>(null);
  const [quoteCodeRule, _setQuoteCodeRule] = useState<QuoteCodeRule>(
    DEFAULT_QUOTE_CODE_RULE,
  );
  const [manualTotalEdited, setManualTotalEdited] = useState(false);
  const autoTotalLockRef = React.useRef(false);
  const { memberOptions: sharedMemberOptions, memberNameMap } =
    useMemberDirectory();
  const productCenter = useOpenPlatformConfig();

  const isEdit = mode === 'edit';
  const lockCustomer = Boolean(context?.lockCustomer && context?.customerUuid);
  const modalWidth = getResponsiveModalWidth('wideForm');

  const watchedDiscountPercent = Form.useWatch('discountPercent', form);
  const watchedTotalPrice = Form.useWatch('totalPrice', form);
  const normalizedRows = useMemo(
    () => normalizeProductRows(productRows),
    [productRows],
  );
  const totalProductPriceNum = useMemo(
    () =>
      normalizedRows.reduce((sum, row) => sum + toNumber(row.totalPrice), 0),
    [normalizedRows],
  );
  const discountPercentNum = toNumber(watchedDiscountPercent);
  const computedTotalPriceNum =
    totalProductPriceNum * Math.max(0, 1 - discountPercentNum / 100);
  const finalTotalPriceNum =
    watchedTotalPrice === undefined ||
    watchedTotalPrice === null ||
    watchedTotalPrice === ''
      ? computedTotalPriceNum
      : toNumber(watchedTotalPrice);
  const editableProductRows = useMemo<EditableProductRow[]>(() => {
    const rows = normalizedRows.map((item) =>
      normalizeEditableProductRow({
        ...item,
        isNew: false,
      }),
    );
    if (editingProductKey === NEW_PRODUCT_ROW_KEY && productDraftRow) {
      return [...rows, productDraftRow];
    }
    return rows.map((item) =>
      productDraftRow && item.key === editingProductKey
        ? productDraftRow
        : item,
    );
  }, [editingProductKey, normalizedRows, productDraftRow]);

  const _resolveMemberName = (record: QuoteItem) =>
    resolveRelationText({
      value: record?.ownerUserUuid,
      directName: record?.ownerMobile,
      nameMap: memberNameMap,
    });

  const getCodePreview = () => {
    const now = dayjs();
    let datePart = '';
    if (quoteCodeRule.dateFormat === 'YYMM') datePart = now.format('YYMM');
    if (quoteCodeRule.dateFormat === 'YYYYMMDD')
      datePart = now.format('YYYYMMDD');
    return `${quoteCodeRule.prefix}${datePart}${'0'.repeat(quoteCodeRule.seqLength)}`;
  };

  const setAutoTotalPrice = (value: string) => {
    autoTotalLockRef.current = true;
    form.setFieldValue('totalPrice', value);
    Promise.resolve().then(() => {
      autoTotalLockRef.current = false;
    });
  };

  const stopProductEditing = () => {
    setEditingProductKey(undefined);
    setProductDraftRow(undefined);
  };

  const resetQuoteFormState = () => {
    form.resetFields();
    setProductRows([]);
    setManualTotalEdited(false);
    setHistoryCustomerUuid(
      lockCustomer
        ? String(context?.customerUuid || '').trim() || undefined
        : undefined,
    );
    stopProductEditing();
  };

  const loadCustomers = async (keyword = '') => {
    const resp = await request('/bff/zy-crm/customers', {
      method: 'GET',
      params: { page: 1, pageSize: 40, keyword: keyword || undefined },
    });
    const list = readDataList(resp);
    const options = list
      .map((item: any) => {
        const value = String(item?.uuid || '').trim();
        if (!value) return null;
        return {
          value,
          label: String(item?.name || value),
          raw: item,
        };
      })
      .filter(Boolean) as OptionItem[];
    setCustomerOptions(
      mergeOptionItem(options, context?.customerUuid, context?.customerName),
    );
  };

  const loadContacts = async (keyword = '', customerUuid?: string) => {
    const resp = await request('/bff/zy-crm/contacts', {
      method: 'GET',
      params: {
        page: 1,
        pageSize: 40,
        keyword: keyword || undefined,
        customerUuid: customerUuid || undefined,
      },
    });
    const list = readDataList(resp);
    const options = list
      .map((item: any) => {
        const value = String(item?.uuid || '').trim();
        if (!value) return null;
        return {
          value,
          label: String(item?.name || item?.mobile || value),
          raw: item,
        };
      })
      .filter(Boolean) as OptionItem[];
    setContactOptions(options);
  };

  const loadOpportunities = async (keyword = '', customerUuid?: string) => {
    const resp = await request('/bff/zy-crm/opportunities', {
      method: 'GET',
      params: {
        page: 1,
        pageSize: 40,
        keyword: keyword || undefined,
        customerUuid: customerUuid || undefined,
      },
    });
    const list = readDataList(resp);
    const options = list
      .map((item: any) => {
        const value = String(item?.uuid || '').trim();
        if (!value) return null;
        return { value, label: String(item?.name || value), raw: item };
      })
      .filter(Boolean) as OptionItem[];
    setOpportunityOptions(options);
  };

  const handleProductSelected = (
    row: ProductCenterRow,
    selection: ProductCenterSelection,
  ) => {
    setProductDraftRow((current) =>
      current && current.key === row.key
        ? normalizeEditableProductRow({
            ...current,
            ...selection,
            quotePrice: selection.standardPrice,
          })
        : current,
    );
  };

  const preloadSelectorData = async (customerUuid?: string) => {
    await Promise.all([
      loadCustomers(),
      loadContacts('', customerUuid),
      loadOpportunities('', customerUuid),
    ]);
  };

  const applyOpportunityProducts = async (opportunityUuid?: string) => {
    const normalizedUuid = String(opportunityUuid || '').trim();
    if (!normalizedUuid) {
      setProductRows([]);
      return;
    }
    try {
      const profile = await queryOpportunityDrawerProfile(normalizedUuid, {
        viewMode: pageViewMode,
      });
      const sourceRows = profile?.related?.products?.list || [];
      const rows = normalizeProductRows(
        sourceRows.map((item: any, index: number) =>
          mapOpportunityProductToRow(item, `${normalizedUuid}-${index}`),
        ),
      );
      if (!rows.length) {
        message.warning('当前商机下没有可带入的产品明细');
        return;
      }
      setProductRows(rows);
      setManualTotalEdited(false);
      message.success('已带入商机产品明细');
    } catch (_error) {
      message.error('商机产品明细加载失败，请稍后重试');
    }
  };

  const handleOpportunityChange = async (value?: string) => {
    const nextUuid = String(value || '').trim();
    if (!nextUuid) {
      if (!hasMeaningfulProductRows(productRows)) {
        setProductRows([]);
      }
      return;
    }
    if (!hasMeaningfulProductRows(productRows)) {
      await applyOpportunityProducts(nextUuid);
      return;
    }
    const confirmed = await new Promise<boolean>((resolve) => {
      Modal.confirm({
        closable: { 'aria-label': '关闭' },
        keyboard: true,
        title: '替换产品明细确认',
        content: '切换商机后，将用商机产品覆盖当前明细，是否继续？',
        okText: '继续替换',
        cancelText: '保留当前明细',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (confirmed) {
      await applyOpportunityProducts(nextUuid);
    }
  };

  const addProductRow = () => {
    if (editingProductKey) {
      message.warning('请先保存或取消当前编辑的产品');
      return;
    }
    setEditingProductKey(NEW_PRODUCT_ROW_KEY);
    setProductDraftRow(
      normalizeEditableProductRow({
        key: NEW_PRODUCT_ROW_KEY,
        productName: '',
        quantity: '1.00',
        standardPrice: '0.00',
        quotePrice: '0.00',
        totalPrice: '0.00',
        isNew: true,
      }),
    );
  };

  const removeProductRow = (key: string) => {
    setProductRows((prev) => prev.filter((item) => item.key !== key));
  };

  const handleProductDraftChange = (
    patch:
      | Partial<EditableProductRow>
      | ((current: EditableProductRow) => Partial<EditableProductRow>),
  ) => {
    setProductDraftRow((current) => {
      const base = normalizeEditableProductRow(current);
      const nextPatch = typeof patch === 'function' ? patch(base) : patch;
      return normalizeEditableProductRow({
        ...base,
        ...nextPatch,
        key: base.key,
        isNew: base.isNew,
      });
    });
  };

  const beginEditProductRow = (record: ProductCenterRow) => {
    if (editingProductKey && editingProductKey !== record.key) {
      message.warning('请先保存或取消当前编辑的产品');
      return;
    }
    setEditingProductKey(record.key);
    setProductDraftRow(
      normalizeEditableProductRow({ ...record, isNew: false }),
    );
  };

  const saveProductRow = () => {
    if (!productDraftRow || !editingProductKey) {
      return;
    }
    if (!String(productDraftRow.offeringVersionUuid || '').trim()) {
      message.error('请选择产品名称');
      return;
    }
    const nextRow = normalizeProductRows([
      {
        ...productDraftRow,
        key:
          editingProductKey === NEW_PRODUCT_ROW_KEY
            ? `${Date.now()}-${Math.random()}`
            : editingProductKey,
      },
    ])[0];

    if (editingProductKey === NEW_PRODUCT_ROW_KEY) {
      setProductRows((prev) => [...prev, nextRow]);
    } else {
      setProductRows((prev) =>
        normalizeProductRows(
          prev.map((item) => {
            if (item.key !== editingProductKey) return item;
            return nextRow;
          }),
        ),
      );
    }
    stopProductEditing();
  };

  const deleteProductRow = (record: ProductRow) => {
    if (record.key === NEW_PRODUCT_ROW_KEY) {
      stopProductEditing();
      return;
    }
    removeProductRow(record.key);
    if (editingProductKey === record.key) {
      stopProductEditing();
    }
  };

  const openCreate = async () => {
    resetQuoteFormState();
    form.setFieldsValue({
      quoteDate: dayjs(),
      ownerUserUuid: context?.ownerUserUuid,
      ownerMobile: undefined,
      discountPercent: 0,
      totalPrice: toAmount(0),
      sourceQuoteUuid: undefined,
      customerUuid: context?.customerUuid,
      contactUuid: context?.contactUuid,
      opportunityUuid: context?.opportunityUuid,
      name: context?.opportunityName
        ? `${context.opportunityName}报价`
        : context?.customerName
          ? `${context.customerName}报价`
          : undefined,
    });
    setProductRows([]);
    setHistoryCustomerUuid(
      lockCustomer
        ? String(context?.customerUuid || '').trim() || undefined
        : undefined,
    );
    if (context?.opportunityUuid) {
      await applyOpportunityProducts(context.opportunityUuid);
    }
    await Promise.all([preloadSelectorData(context?.customerUuid)]);
  };

  const openEdit = async () => {
    if (!quoteUuid) return;
    const resp = await request(`/bff/zy-crm/quotes/${quoteUuid}`, {
      method: 'GET',
    });
    const detail = readDetailPayload(resp);
    if (!detail) {
      message.error('报价详情加载失败');
      return;
    }
    const rows = normalizeProductRows(
      (detail?.products || []).map((item: any, index: number) =>
        mapQuoteProductToRow(item, String(index + 1)),
      ),
    );
    setProductRows(rows);
    setManualTotalEdited(true);
    form.setFieldsValue({
      ...detail,
      quoteDate: toDateValue(detail?.quoteDate),
      expectedDealDate: toDateValue(detail?.expectedDealDate),
      discountPercent: toNumber(detail?.discountPercent),
      totalPrice: toAmount(toNumber(detail?.totalPrice)),
      quoteFollowUpStatus: detail?.quoteFollowUpStatus || 'pending_feedback',
      quoteLastFeedbackTime: toDateValue(detail?.quoteLastFeedbackTime),
      quoteLostReason: detail?.quoteLostReason,
    });
    await Promise.all([
      preloadSelectorData(
        detail?.customerUuid ? String(detail.customerUuid) : undefined,
      ),
    ]);
  };

  const _openHistory = () => {
    setHistoryOpen(true);
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const resp = await request('/bff/zy-crm/quotes/history', {
        method: 'GET',
        params: {
          range: historyRange,
          page: historyPage,
          pageSize: historyPageSize,
          sortBy: historySortBy,
          sortOrder: historySortOrder,
          customerUuid: historyCustomerUuid || undefined,
          keyword: historyKeyword || undefined,
        },
      });
      const list = readDataList(resp);
      setHistoryList(list as QuoteItem[]);
      setHistoryTotal(readDataTotal(resp, list.length));
    } catch (_error) {
      setHistoryList([]);
      setHistoryTotal(0);
      message.error('历史报价加载失败');
    } finally {
      setHistoryLoading(false);
    }
  };

  const applyHistoryQuote = async (
    historyRecord: QuoteItem,
    forceCustomer = false,
  ) => {
    const currentCustomer = String(
      form.getFieldValue('customerUuid') || '',
    ).trim();
    const sourceCustomer = String(historyRecord.customerUuid || '').trim();
    if (
      lockCustomer &&
      currentCustomer &&
      sourceCustomer &&
      currentCustomer !== sourceCustomer
    ) {
      message.warning('当前仅可复用该客户名下的历史报价');
      return;
    }
    if (
      !forceCustomer &&
      currentCustomer &&
      sourceCustomer &&
      currentCustomer !== sourceCustomer
    ) {
      Modal.confirm({
        closable: { 'aria-label': '关闭' },
        keyboard: true,
        title: '跨客户复用确认',
        content: '当前客户与历史报价客户不一致，确认继续将覆盖客户信息吗？',
        onOk: async () => applyHistoryQuote(historyRecord, true),
      });
      return;
    }
    const resp = await request(`/bff/zy-crm/quotes/${historyRecord.uuid}`, {
      method: 'GET',
    });
    const detail = readDetailPayload(resp);
    if (!detail) {
      message.error('报价详情加载失败');
      return;
    }
    const rows = normalizeProductRows(
      (detail?.products || []).map((item: any, index: number) =>
        mapQuoteProductToRow(item, `${Date.now()}-${index}`),
      ),
    );
    setProductRows(rows);
    setManualTotalEdited(true);
    form.setFieldsValue({
      name: detail?.name,
      customerUuid: lockCustomer
        ? currentCustomer || detail?.customerUuid
        : detail?.customerUuid,
      contactUuid: detail?.contactUuid,
      opportunityUuid: detail?.opportunityUuid,
      ownerUserUuid: detail?.ownerUserUuid,
      ownerMobile: detail?.ownerMobile,
      quoteDate: dayjs(),
      expectedDealDate: toDateValue(detail?.expectedDealDate),
      discountPercent: toNumber(detail?.discountPercent),
      totalPrice: toAmount(toNumber(detail?.totalPrice)),
      remark: detail?.remark,
      sourceQuoteUuid: detail?.uuid,
    });
    if (detail?.customerUuid) {
      const nextCustomerUuid = lockCustomer
        ? currentCustomer || String(context?.customerUuid || '').trim()
        : String(detail.customerUuid);
      await Promise.all([
        loadContacts('', nextCustomerUuid),
        loadOpportunities('', nextCustomerUuid),
      ]);
    }
    setHistoryOpen(false);
    message.success('历史报价复用成功');
  };

  const submitModal = async () => {
    const values = await form.validateFields();
    const rows = normalizeProductRows(productRows).filter(
      (item) =>
        item.offeringVersionUuid &&
        item.productName &&
        toNumber(item.quantity) > 0,
    );
    if (!rows.length) {
      message.error('请至少添加一个产品明细');
      return;
    }
    const discount = toNumber(values?.discountPercent);
    const computedTotal =
      rows.reduce((sum, row) => sum + toNumber(row.totalPrice), 0) *
      Math.max(0, 1 - discount / 100);
    const manualTotal = toNumber(values?.totalPrice);
    const diff = Math.abs(manualTotal - computedTotal);

    let confirmedDiff = false;
    if (diff > TOTAL_DIFF_THRESHOLD) {
      const confirmed = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          closable: { 'aria-label': '关闭' },
          keyboard: true,
          title: '报价金额差异确认',
          content: `手工总额与明细计算值差异为 ${toAmount(diff)}，确认继续提交吗？`,
          okText: '确认',
          cancelText: '取消',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!confirmed) {
        message.warning('金额差异未确认，已取消提交');
        return;
      }
      confirmedDiff = true;
    }

    const payload = {
      name: String(values?.name || '').trim(),
      customerUuid: values?.customerUuid,
      contactUuid: values?.contactUuid || undefined,
      opportunityUuid: values?.opportunityUuid || undefined,
      quoteDate: toDatePayload(values?.quoteDate),
      expectedDealDate: toDatePayload(values?.expectedDealDate),
      ownerUserUuid: values?.ownerUserUuid,
      ownerMobile: String(values?.ownerMobile || '').trim(),
      discountPercent: toAmount(toNumber(values?.discountPercent)),
      totalPrice: toAmount(toNumber(values?.totalPrice)),
      confirmTotalPriceDiff: confirmedDiff,
      remark: values?.remark || undefined,
      sourceQuoteUuid: values?.sourceQuoteUuid || undefined,
      quoteFollowUpStatus: values?.quoteFollowUpStatus || undefined,
      quoteLastFeedbackTime: toDatePayload(values?.quoteLastFeedbackTime),
      quoteLostReason: values?.quoteLostReason || undefined,
      products: rows.map(toQuoteApiProductRow),
    };

    setSubmitting(true);
    try {
      const resp =
        isEdit && quoteUuid
          ? await request(`/bff/zy-crm/quotes/${quoteUuid}`, {
              method: 'PATCH',
              data: payload,
            })
          : await request('/bff/zy-crm/quotes', {
              method: 'POST',
              data: payload,
            });
      message.success(isEdit ? '更新成功' : '创建成功');
      onSuccess(resp as QuoteItem);
      resetQuoteFormState();
      onCancel();
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      void openEdit();
    } else {
      void openCreate();
    }
  }, [context, isEdit, open, quoteUuid]);

  useEffect(() => {
    if (!open) {
      resetQuoteFormState();
      setHistoryOpen(false);
      setVersionOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || manualTotalEdited) return;
    setAutoTotalPrice(toAmount(computedTotalPriceNum));
  }, [computedTotalPriceNum, manualTotalEdited, open]);

  useEffect(() => {
    if (!historyOpen) return;
    void fetchHistory();
  }, [
    historyOpen,
    historyRange,
    historyKeyword,
    historyCustomerUuid,
    historyPage,
    historyPageSize,
    historySortBy,
    historySortOrder,
  ]);

  const sharedProductRows = useMemo(
    () => editableProductRows,
    [editableProductRows],
  );

  return (
    <>
      <Modal
        closable={{ 'aria-label': '关闭' }}
        keyboard
        className={`${modalStyles.modal} ${modalStyles.form}`}
        centered
        title={isEdit ? '编辑报价单' : '新建报价单'}
        open={open}
        width={modalWidth}
        zIndex={zIndex}
        onCancel={onCancel}
        onOk={submitModal}
        confirmLoading={submitting}
        destroyOnHidden
        forceRender
      >
        <Form
          className={modalStyles.formGrid}
          form={form}
          layout="vertical"
          colon={false}
          onValuesChange={(changedValues, allValues) => {
            if (Object.hasOwn(changedValues, 'totalPrice')) {
              if (!autoTotalLockRef.current) {
                setManualTotalEdited(true);
              }
            }
            if (Object.hasOwn(changedValues, 'discountPercent')) {
              if (!manualTotalEdited) {
                setAutoTotalPrice(toAmount(computedTotalPriceNum));
              }
            }
            if (Object.hasOwn(changedValues, 'customerUuid')) {
              const customerUuid = allValues?.customerUuid
                ? String(allValues.customerUuid)
                : undefined;
              form.setFieldsValue({
                contactUuid: undefined,
                opportunityUuid: undefined,
              });
              void loadContacts('', customerUuid);
              void loadOpportunities('', customerUuid);
            }
            if (Object.hasOwn(changedValues, 'opportunityUuid')) {
              void handleOpportunityChange(
                String(allValues?.opportunityUuid || ''),
              );
            }
            if (Object.hasOwn(changedValues, 'ownerUserUuid')) {
              const ownerUuid = String(allValues?.ownerUserUuid || '');
              if (!ownerUuid) return;
              const selected = sharedMemberOptions.find(
                (item) => item.value === ownerUuid,
              );
              const rawMember = selected?.raw as
                | { phone?: unknown }
                | undefined;
              const phone = String(rawMember?.phone || '').trim();
              if (phone) {
                form.setFieldValue('ownerMobile', phone);
              }
            }
          }}
        >
          <FormSectionDivider title="基础信息" />
          <Row gutter={12}>
            <Col data-crm-field="medium" xs={24} md={12}>
              <Form.Item
                label="报价单名称"
                name="name"
                rules={[{ required: true, message: '请输入报价单名称' }]}
              >
                <Input maxLength={255} />
              </Form.Item>
            </Col>
            <Col data-crm-field="medium" xs={24} md={12}>
              <Form.Item label="报价编号">
                <Input value={quoteUuid || getCodePreview()} disabled />
              </Form.Item>
            </Col>
            <Col data-crm-field="medium" xs={24} md={12}>
              <Form.Item
                label="客户名称"
                name="customerUuid"
                rules={[{ required: true, message: '请选择客户' }]}
              >
                <Select
                  showSearch
                  filterOption={false}
                  options={customerOptions}
                  onSearch={loadCustomers}
                  placeholder="请选择客户"
                  disabled={lockCustomer}
                />
              </Form.Item>
            </Col>
            <Col data-crm-field="medium" xs={24} md={12}>
              <Form.Item label="客户联系人" name="contactUuid">
                <Select
                  showSearch
                  allowClear
                  filterOption={false}
                  options={contactOptions}
                  onSearch={(value) =>
                    loadContacts(
                      value,
                      String(form.getFieldValue('customerUuid') || ''),
                    )
                  }
                  placeholder="请选择客户联系人"
                />
              </Form.Item>
            </Col>
            <Col data-crm-field="medium" xs={24} md={12}>
              <Form.Item label="商机名称" name="opportunityUuid">
                <Select
                  showSearch
                  allowClear
                  filterOption={false}
                  options={opportunityOptions}
                  onSearch={(value) =>
                    loadOpportunities(
                      value,
                      String(form.getFieldValue('customerUuid') || ''),
                    )
                  }
                  placeholder="请选择商机名称"
                />
              </Form.Item>
            </Col>
            <Col data-crm-field="short" xs={24} md={12}>
              <Form.Item
                label="负责人"
                name="ownerUserUuid"
                rules={[{ required: true, message: '请选择负责人' }]}
              >
                <MemberPickerField adapter={orgDirectoryAdapter} />
              </Form.Item>
            </Col>
            <Col data-crm-field="medium" xs={24} md={12}>
              <Form.Item
                label="负责人手机号"
                name="ownerMobile"
                rules={[{ required: true, message: '请输入负责人手机号' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <FormSectionDivider title="时间与状态" />
          <Row gutter={12}>
            <Col data-crm-field="short" xs={24} md={12}>
              <Form.Item
                label="报价日期"
                name="quoteDate"
                rules={[{ required: true, message: '请选择报价日期' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col data-crm-field="medium" xs={24} md={12}>
              <Form.Item label="预计成交日期" name="expectedDealDate">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col data-crm-field="medium" xs={24} md={12}>
              <Form.Item label="跟进状态" name="quoteFollowUpStatus">
                <Select
                  allowClear
                  options={[
                    { label: '待反馈', value: 'pending_feedback' },
                    { label: '沟通中', value: 'communicating' },
                    { label: '已拒绝', value: 'rejected' },
                    { label: '进入合同', value: 'converted' },
                    { label: '失效', value: 'inactive' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col data-crm-field="medium" xs={24} md={12}>
              <Form.Item label="最近反馈时间" name="quoteLastFeedbackTime">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col data-crm-field="medium" xs={24} md={12}>
              <Form.Item label="输单原因" name="quoteLostReason">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <ProductCenterFormSection
            title="产品明细"
            rows={sharedProductRows}
            editingRowKey={editingProductKey}
            onBeginEdit={beginEditProductRow}
            onSaveEdit={saveProductRow}
            onCancelEdit={stopProductEditing}
            onDraftChange={handleProductDraftChange}
            onDeleteRow={deleteProductRow}
            productCenter={productCenter}
            transactionPrice="quotePrice"
            transactionPriceTitle="报价价"
            onProductSelected={handleProductSelected}
            scroll={{ x: 980 }}
            addButtonText="新增一条产品"
            onAdd={addProductRow}
          />
          <FormSectionDivider title="金额汇总" />
          <Row gutter={12}>
            <Col data-crm-field="short" xs={24} md={12}>
              <Form.Item label="整单折扣(%)" name="discountPercent">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col data-crm-field="short" xs={24} md={12}>
              <Form.Item
                label="报价金额"
                name="totalPrice"
                rules={[{ required: true, message: '请输入报价金额' }]}
              >
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col data-crm-field="medium" xs={24} md={12}>
              <Form.Item label="来源报价单" name="sourceQuoteUuid">
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>
          <Space orientation="vertical" style={{ width: '100%' }} size={4}>
            <div>
              已选中产品：{normalizedRows.length} 种{'  '}
              总金额：{toAmount(finalTotalPriceNum)} 元
            </div>
          </Space>
          <FormSectionDivider title="备注说明" />
          <Form.Item label="备注" name="remark">
            <Input.TextArea rows={4} maxLength={800} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <BusinessModal
        title="历史报价"
        open={historyOpen}
        onCancel={() => setHistoryOpen(false)}
        destroyOnHidden
      >
        <Space orientation="vertical" style={{ width: '100%' }} size={12}>
          <Select
            value={historyRange}
            onChange={(value) => {
              setHistoryRange(value);
              setHistoryPage(1);
            }}
            options={[
              { value: 'lastMonth', label: '最近一个月' },
              { value: 'halfYear', label: '近半年' },
              { value: 'thisYear', label: '本年度' },
            ]}
          />
          <Space.Compact style={{ width: '100%' }}>
            <Select
              style={{ width: 160 }}
              allowClear
              showSearch
              filterOption={false}
              options={customerOptions}
              value={historyCustomerUuid}
              onSearch={loadCustomers}
              onChange={(value) => {
                setHistoryCustomerUuid(value);
                setHistoryPage(1);
              }}
              placeholder="客户名称"
              disabled={lockCustomer}
            />
            <Input
              value={historyKeywordInput}
              onChange={(e) => setHistoryKeywordInput(e.target.value)}
              placeholder="请输入关键字"
              onPressEnter={() => {
                setHistoryKeyword(historyKeywordInput.trim());
                setHistoryPage(1);
              }}
            />
            <Button
              onClick={() => {
                setHistoryKeyword(historyKeywordInput.trim());
                setHistoryPage(1);
              }}
            >
              搜索
            </Button>
          </Space.Compact>
          <Table
            scroll={{ x: 'max-content' }}
            rowKey="uuid"
            size="small"
            loading={historyLoading}
            dataSource={historyList}
            columns={
              [
                { title: '报价单编号', dataIndex: 'code' },
                { title: '报价单名称', dataIndex: 'name' },
                {
                  title: '客户名称',
                  dataIndex: 'customerUuid',
                  render: (_: unknown, row: QuoteItem) => (
                    <CustomerReferenceLink
                      customerUuid={row?.customerUuid}
                      customerName={resolveRelationText({
                        value: row?.customerUuid,
                        directName: row?.customerName,
                        nameMap: {},
                      })}
                    />
                  ),
                },
                {
                  title: '商机名称',
                  dataIndex: 'opportunityUuid',
                  render: (_: unknown, row: QuoteItem) => (
                    <OpportunityReferenceLink
                      opportunityUuid={row?.opportunityUuid}
                      opportunityName={
                        row?.opportunityName || row?.opportunityDisplayName
                      }
                    />
                  ),
                },
                {
                  title: '报价日期',
                  dataIndex: 'quoteDate',
                  render: (value: string) => formatCrmDate(value, 'date'),
                },
                { title: '报价金额', dataIndex: 'totalPrice' },
                {
                  title: '操作',
                  render: (_: unknown, row: QuoteItem) => (
                    <a onClick={() => void applyHistoryQuote(row)}>复用</a>
                  ),
                },
              ] as any
            }
            pagination={{
              current: historyPage,
              pageSize: historyPageSize,
              total: historyTotal,
              showSizeChanger: true,
              onChange: (page, pageSize) => {
                setHistoryPage(page);
                setHistoryPageSize(pageSize);
              },
            }}
            onChange={(pagination: any, _filters, sorter: any) => {
              if (pagination?.current && pagination.current !== historyPage) {
                setHistoryPage(pagination.current);
              }
              const order = sorter?.order;
              const field = String(sorter?.field || 'quoteDate') as
                | 'quoteDate'
                | 'createdAt'
                | 'totalPrice'
                | 'code';
              const allowed = ['quoteDate', 'createdAt', 'totalPrice', 'code'];
              if (allowed.includes(field)) setHistorySortBy(field);
              if (order === 'ascend') setHistorySortOrder('ASC');
              if (order === 'descend') setHistorySortOrder('DESC');
            }}
            locale={{ emptyText: <QuietEmpty /> }}
          />
        </Space>
      </BusinessModal>

      <BusinessModal
        widthPreset="detail"
        title="报价版本对比"
        open={versionOpen}
        onCancel={() => setVersionOpen(false)}
        destroyOnHidden
      >
        <Space orientation="vertical" style={{ width: '100%' }} size={12}>
          <Table
            scroll={{ x: 'max-content' }}
            rowKey="uuid"
            size="small"
            loading={versionLoading}
            pagination={false}
            dataSource={versionTimeline}
            columns={
              [
                { title: '版本', dataIndex: 'quoteVersionNo', width: 80 },
                { title: '报价单', dataIndex: 'name' },
                { title: '报价编号', dataIndex: 'code' },
                {
                  title: '报价金额',
                  dataIndex: 'totalPrice',
                  render: (value: string) => toAmount(toNumber(value)),
                },
                {
                  title: '折扣',
                  dataIndex: 'discountPercent',
                  render: (value: string) => `${toAmount(toNumber(value))}%`,
                },
              ] as any
            }
            locale={{ emptyText: <QuietEmpty /> }}
          />
          {versionDiff ? null : null}
        </Space>
      </BusinessModal>
    </>
  );
}
